import { AgGridColumn, AgGridReact } from 'ag-grid-react';
import { useEffect, useRef, useState } from 'react';

import theme from '../../themes/theme';
import {
    columnEventTypes,
    columnTypes,
    listTypes,
    localStorageKey,
    tabHeadToSymbols,
    tabHeadToSymbolsToTitles,
    tableTranslations,
    waterpipeWater,
} from '../../utils/constants';
import { isColumnHighlighted } from '../../utils/helpers';
import SettingsPanel from '../GeneratedList/SettingsPanel/SettingsPanel';
import PaginationRows from '../Pagination/PaginationRows';

const styles = {
    root: {
        margin: '1rem 0',
    },
    table: {
        fontFamily: theme.font.family,
        position: 'relative',
        height: '100%',
    },
    errorMessage: {
        color: 'red',
        fontWeight: theme.font.weight.bold,
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        userSelect: 'none',
    },
};

const columnsList = [
    { field: columnTypes.ORDINAL_NUMBER, width: 60 },
    { field: columnTypes.SERIAL_NUMBER_ID, width: 90 },
    { field: columnTypes.SERIAL_NUMBER_CREATED_AT, width: 160 },
    { field: columnTypes.SERIAL_NUMBER_UPDATED_AT, width: 160 },
    { field: columnTypes.SERIAL_NUMBER_NUMBER, width: 120 },
    { field: columnTypes.SERIAL_NUMBER_SYMBOL, width: 120 },
    { field: columnTypes.SERIAL_NUMBER_SIGN, width: 150 },
    { field: columnTypes.SERIAL_NUMBER_FIRST_REPORT_AT, width: 200 },
    { field: columnTypes.SERIAL_NUMBER_FIRMWARE, width: 140 },
    { field: columnTypes.SERIAL_NUMBER_WATERWORKS_NUMBER, width: 120 },
    { field: columnTypes.SERIAL_NUMBER_APN, width: 140 },
];

const buildDefaultColumns = () =>
    columnsList.map(col => ({
        field: col.field,
        headerName: tabHeadToSymbols[col.field],
        headerTooltip: tabHeadToSymbolsToTitles[col.field],
        width: col.width,
        hide: false,
    }));

const PAGINATION_SIZES = [10, 50, 100, 500, 1000];

export default function TableSerialNumbers({ data, isFetching, isErrorLoadingData, waterpipesList = [] }) {
    const settingsPanelRef = useRef(null);
    const paginationRef = useRef(null);
    const [gridApi, setGridApi] = useState(null);
    const [columnsDefs, setColumnsDefs] = useState([]);
    const [paginationSize, setPaginationSize] = useState(
        localStorage.getItem(localStorageKey.PAGINATION_SIZE_SERIAL_NUMBERS) ?? 100
    );
    const [tableHeight, setTableHeight] = useState();
    const [settingsPanelPosition, setSettingsPanelPosition] = useState(50);

    const onGridReady = params => setGridApi(params.api);

    const cellRenderer = params => {
        const columnType = params.colDef.field;

        if (columnType === columnTypes.SERIAL_NUMBER_NUMBER) {
            const sign = params.data?.[columnTypes.SERIAL_NUMBER_SIGN];
            const paddedSign = sign?.length === 1 ? `0${sign}` : sign;

            let waterpipeId = waterpipesList.filter(w => w.sign === paddedSign)[0]?.id;

            if (waterpipeId === null || waterpipeId === undefined || waterpipeId === '') {
                waterpipeId = waterpipesList.filter(w => w.sign === waterpipeWater.sign)[0]?.id;
            }

            if (waterpipeId === null || waterpipeId === undefined || waterpipeId === '') {
                return params.value ?? '';
            }

            const cellPanel = document.createElement('a');
            cellPanel.href = `#/pipeline/${waterpipeId}/reports`;
            cellPanel.style.color = theme.colors.link;
            cellPanel.style.textDecoration = 'none';
            cellPanel.onclick = () => {
                localStorage.setItem(localStorageKey.DOCS_SEARCH_COMMUNICATOR, params.value ?? '');
                localStorage.setItem(localStorageKey.DOCS_SEARCH_PACKAGE_ID, '');
            };
            cellPanel.target = '_blank';
            cellPanel.rel = 'noopener noreferrer';
            cellPanel.appendChild(document.createTextNode(params.value ?? ''));

            return cellPanel;
        }

        return params.value ?? '';
    };

    useEffect(() => {
        let colDefs = JSON.parse(localStorage.getItem(localStorageKey.COLUMNS_DEFS_SERIAL_NUMBERS));

        if (!colDefs?.length || colDefs.some(col => !col.field)) {
            colDefs = buildDefaultColumns();
        } else {
            const savedFields = new Set(colDefs.map(col => col.field));
            const newColumns = buildDefaultColumns().filter(col => !savedFields.has(col.field));

            if (newColumns.length) {
                colDefs = [...colDefs, ...newColumns];
            }
        }

        setColumnsDefs(colDefs);
    }, []);

    const saveColumnSettings = params => {
        localStorage.setItem(localStorageKey.COLUMNS_DEFS_SERIAL_NUMBERS, JSON.stringify(params));
        setColumnsDefs(params);
    };

    const setColumnHide = params => {
        if (gridApi) {
            params.forEach(column => {
                gridApi.columnModel.columnApi.setColumnVisible(column.field, !column.hide);
            });
            saveColumnSettings(params);
        }
    };

    const onColumnChange = event => {
        if (
            gridApi &&
            ((event?.type === columnEventTypes.resize && event?.finished) ||
                event?.type === columnEventTypes.move)
        ) {
            saveColumnSettings(gridApi.columnModel.getColumnDefs());
        }
    };

    useEffect(() => {
        if (!settingsPanelRef.current || !paginationRef.current) return undefined;

        const updateHeight = () => {
            const settingsPanelHeight = settingsPanelRef.current?.clientHeight ?? 0;
            const paginationHeight = paginationRef.current?.clientHeight ?? 0;
            const newHeight = `calc(100% - ${settingsPanelHeight + paginationHeight}px - 2rem)`;
            setTableHeight(newHeight);
            setSettingsPanelPosition(settingsPanelHeight);
        };

        updateHeight();
        window.addEventListener('resize', updateHeight);
        return () => window.removeEventListener('resize', updateHeight);
    }, []);

    useEffect(() => {
        if (gridApi) {
            if (isFetching) {
                gridApi.showLoadingOverlay();
            } else if (!isFetching && !data?.length) {
                gridApi.showNoRowsOverlay();
            } else {
                gridApi.hideOverlay();
            }
        }
    }, [gridApi, data, isFetching]);

    const changeRowStyle = params => {
        params.api.refreshCells();
        if (gridApi) gridApi.redrawRows();
    };

    const changePaginationSize = event => {
        setPaginationSize(event.target.value);
    };

    useEffect(() => {
        localStorage.setItem(localStorageKey.PAGINATION_SIZE_SERIAL_NUMBERS, paginationSize);
    }, [paginationSize]);

    return (
        <div style={{ ...styles.root, height: tableHeight }}>
            <SettingsPanel
                ref={settingsPanelRef}
                columnsDefs={columnsDefs}
                setColumnHide={setColumnHide}
                gridApi={gridApi}
                listType={listTypes.SERIAL_NUMBERS}
                settingsPanelPosition={settingsPanelPosition}
                hideFilters
                hideExport
                copyData={columnTypes.SERIAL_NUMBER_NUMBER}
                isFetching={isFetching}
            />
            <div className={'ag-theme-alpine'} style={styles.table}>
                <AgGridReact
                    localeText={tableTranslations}
                    onSortChanged={changeRowStyle}
                    onFilterChanged={changeRowStyle}
                    getRowStyle={params => isColumnHighlighted(params.node.rowIndex)}
                    rowData={Array.isArray(data) ? data : []}
                    onGridReady={onGridReady}
                    suppressCellSelection
                    pagination
                    paginationPageSize={paginationSize}
                    onColumnResized={onColumnChange}
                    onColumnMoved={onColumnChange}
                    tooltipShowDelay={0}
                    enableCellTextSelection
                    suppressDragLeaveHidesColumns
                    suppressColumnMoveAnimation
                    accentedSort
                    suppressNoRowsOverlay={!isFetching}
                    onFirstDataRendered={params => params.api.sizeColumnsToFit()}>
                    {columnsDefs.map(column => (
                        <AgGridColumn
                            key={column.field}
                            headerName={column.headerName}
                            headerTooltip={column.headerTooltip}
                            field={column.field}
                            width={column.width}
                            hide={column.hide}
                            sortable={column.field !== columnTypes.ORDINAL_NUMBER}
                            filter={column.field !== columnTypes.ORDINAL_NUMBER}
                            resizable
                            cellStyle={{ textAlign: 'left' }}
                            cellRenderer={
                                column.field === columnTypes.SERIAL_NUMBER_NUMBER ? cellRenderer : undefined
                            }
                            tooltipValueGetter={params => params.value}
                            valueGetter={params => {
                                if (column.field === columnTypes.ORDINAL_NUMBER) {
                                    return params.node.rowIndex + 1;
                                }
                                const value = params.data?.[column.field];
                                if (
                                    (column.field === columnTypes.SERIAL_NUMBER_CREATED_AT ||
                                        column.field === columnTypes.SERIAL_NUMBER_UPDATED_AT ||
                                        column.field === columnTypes.SERIAL_NUMBER_FIRST_REPORT_AT) &&
                                    value
                                ) {
                                    return `${value.slice(0, 10)} ${value.slice(11, 19)}`;
                                }
                                return value;
                            }}
                        />
                    ))}
                </AgGridReact>
                <div ref={paginationRef}>
                    <PaginationRows
                        paginationSizes={PAGINATION_SIZES}
                        paginationSize={paginationSize}
                        changePaginationSize={changePaginationSize}
                    />
                </div>
                {isErrorLoadingData && (
                    <p style={styles.errorMessage}>{tableTranslations.errorLoadingData}</p>
                )}
            </div>
        </div>
    );
}
